import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import Swal from 'sweetalert2'
import { editParents, viewParentDetails } from '../../slice/parentSlice'
import { uploadFileToS3 } from '../../common/uploadFileToS3'
import moment from 'moment'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'

// Validation Schema for Edit (password is optional)
const editParentSchema = yup.object().shape({
    first_name: yup
        .string()
        .required('First name is required')
        .min(2, 'First name must be at least 2 characters')
        .max(50, 'First name cannot exceed 50 characters')
        .matches(/^[a-zA-Z\s]+$/, 'First name can only contain letters'),
    
    last_name: yup
        .string()
        .required('Last name is required')
        .min(2, 'Last name must be at least 2 characters')
        .max(50, 'Last name cannot exceed 50 characters')
        .matches(/^[a-zA-Z\s]+$/, 'Last name can only contain letters'),
    
    username: yup
        .string()
        .required('Username is required')
        .min(3, 'Username must be at least 3 characters')
        .max(30, 'Username cannot exceed 30 characters')
        .matches(/^[a-zA-Z0-9._-]+$/, 'Username can only contain letters, numbers, dots, hyphens, and underscores'),
    
    email: yup
        .string()
        .required('Email is required')
        .email('Please enter a valid email address'),
    
    password: yup
        .string()
        .nullable()
        .transform((value) => value || null)
        .min(8, 'Password must be at least 8 characters')
        .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
            'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
        ),
    
    confirm_password: yup
        .string()
        .nullable()
        .transform((value) => value || null)
        .when('password', {
            is: (val) => val && val.length > 0,
            then: (schema) => schema
                .required('Please confirm your password')
                .oneOf([yup.ref('password')], 'Passwords must match'),
        }),
    
    country_code: yup
        .string()
        .required('Country code is required'),
    
    phone_number: yup
        .string()
        .required('Phone number is required')
        .matches(/^[0-9]{9,15}$/, 'Phone number must be between 9 and 15 digits'),
    
    dob: yup
        .date()
        .required('Date of birth is required')
        .max(new Date(), 'Date of birth cannot be in the future')
        .test('age', 'Parent must be at least 18 years old', function(value) {
            const cutoff = new Date()
            cutoff.setFullYear(cutoff.getFullYear() - 18)
            return value <= cutoff
        }),
    
    lang: yup
        .string()
        .required('Language is required')
        .oneOf(['en', 'ar'], 'Please select a valid language'),
    
    emirates_id_front: yup
        .mixed()
        .nullable()
        .test('fileSize', 'File size must be less than 5MB', (value) => {
            if (!value || !value[0]) return true // Optional for edit
            return value[0].size <= 5242880
        })
        .test('fileType', 'Only image files are allowed (jpg, jpeg, png)', (value) => {
            if (!value || !value[0]) return true
            return ['image/jpeg', 'image/jpg', 'image/png'].includes(value[0].type)
        }),
    
    emirates_id_back: yup
        .mixed()
        .nullable()
        .test('fileSize', 'File size must be less than 5MB', (value) => {
            if (!value || !value[0]) return true
            return value[0].size <= 5242880
        })
        .test('fileType', 'Only image files are allowed (jpg, jpeg, png)', (value) => {
            if (!value || !value[0]) return true
            return ['image/jpeg', 'image/jpg', 'image/png'].includes(value[0].type)
        }),
})

const EditParent = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    
    const parentId = location.state?.parentId
    const { parentDetail, loading } = useSelector(state => state.parent)
    
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [frontPreview, setFrontPreview] = useState(null)
    const [backPreview, setBackPreview] = useState(null)
    const [existingFrontImage, setExistingFrontImage] = useState(null)
    const [existingBackImage, setExistingBackImage] = useState(null)
    const [countryCode, setCountryCode] = useState('971')

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        watch,
        reset,
        setValue
    } = useForm({
        resolver: yupResolver(editParentSchema),
        defaultValues: {
            first_name: '',
            last_name: '',
            username: '',
            email: '',
            password: '',
            confirm_password: '',
            country_code: '+971',
            phone_number: '',
            dob: '',
            lang: 'en',
        }
    })

    // Helper function to extract filename from S3 URL
    const extractFilenameFromUrl = (url) => {
        if (!url) return null
        const parts = url.split('/')
        return parts[parts.length - 1]
    }

    // Fetch parent details on mount
    useEffect(() => {
        if (!parentId) {
            navigate('/parents')
            return
        }
        dispatch(viewParentDetails({ id: parentId }))
    }, [parentId, dispatch, navigate])

    // Populate form when data loads
    useEffect(() => {
        if (parentDetail) {
            setValue('first_name', parentDetail.first_name || '')
            setValue('last_name', parentDetail.last_name || '')
            setValue('username', parentDetail.username || '')
            setValue('email', parentDetail.email || '')
            setValue('country_code', parentDetail.country_code || '+971')
            setValue('phone_number', parentDetail.phone_number || '')
            setValue('dob', parentDetail.dob ? moment(parentDetail.dob).format('YYYY-MM-DD') : '')
            setValue('lang', parentDetail.lang || 'en')
            
            // Set country code for PhoneInput (remove + sign)
            const dialCode = parentDetail.country_code ? parentDetail.country_code.replace('+', '') : '971'
            setCountryCode(dialCode)
            
            // Set existing images (full URL for display)
            setExistingFrontImage(parentDetail.emirates_id_front || null)
            setExistingBackImage(parentDetail.emirates_id_back || null)
        }
    }, [parentDetail, setValue])

    // Watch file inputs for preview
    const emiratesFrontFile = watch('emirates_id_front')
    const emiratesBackFile = watch('emirates_id_back')

    useEffect(() => {
        if (emiratesFrontFile && emiratesFrontFile[0]) {
            const reader = new FileReader()
            reader.onloadend = () => {
                setFrontPreview(reader.result)
            }
            reader.readAsDataURL(emiratesFrontFile[0])
        }
    }, [emiratesFrontFile])

    useEffect(() => {
        if (emiratesBackFile && emiratesBackFile[0]) {
            const reader = new FileReader()
            reader.onloadend = () => {
                setBackPreview(reader.result)
            }
            reader.readAsDataURL(emiratesBackFile[0])
        }
    }, [emiratesBackFile])

    const onSubmit = async (data) => {
        setIsSubmitting(true)

        try {
            // Initialize with existing filenames (extracted from URLs)
            let frontImageFilename = extractFilenameFromUrl(existingFrontImage)
            let backImageFilename = extractFilenameFromUrl(existingBackImage)

            // Upload new front image if selected
            if (data.emirates_id_front && data.emirates_id_front[0]) {
                const frontUpload = await uploadFileToS3(
                    data.emirates_id_front[0],
                    "parent/emirates"
                )
                frontImageFilename = frontUpload.fileName
            }

            // Upload new back image if selected
            if (data.emirates_id_back && data.emirates_id_back[0]) {
                const backUpload = await uploadFileToS3(
                    data.emirates_id_back[0],
                    "parent/emirates"
                )
                backImageFilename = backUpload.fileName
            }

            // Build payload with filenames only
            const payload = {
                id: parentId,
                first_name: data.first_name,
                last_name: data.last_name,
                username: data.username,
                email: data.email,
                country_code: data.country_code,
                phone_number: data.phone_number,
                dob: data.dob,
                lang: data.lang,
                emirates_id_front: frontImageFilename, // ✅ filename only
                emirates_id_back: backImageFilename,   // ✅ filename only
            }

            // Only include password if it was entered
            if (data.password && data.password.trim() !== '') {
                payload.password = data.password
            }

            const response = await dispatch(editParents(payload))

            if (response?.payload?.code === 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Parent updated successfully',
                    timer: 2000,
                    showConfirmButton: false,
                })

                setTimeout(() => navigate('/parents'), 2000)
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response?.payload?.message || 'Failed to update parent',
                })
            }

        } catch (error) {
            console.error(error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.message || 'Something went wrong',
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        )
    }

    if (!parentDetail) {
        return (
            <div className="text-center py-5">
                <i className="fe-alert-circle" style={{ fontSize: '48px', color: '#dc3545' }}></i>
                <h5 className="mt-3">No data found</h5>
                <button 
                    className="btn btn-primary mt-3" 
                    onClick={() => navigate('/parents')}
                >
                    Go Back to Parents
                </button>
            </div>
        )
    }

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Edit Parent</h4>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/parents')}
                    style={{
                        padding: '8px 20px',
                        fontSize: '14px',
                        borderRadius: '6px'
                    }}
                >
                    ← Back to List
                </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="row">
                    {/* Personal Information */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                Personal Information
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* First Name */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            First Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.first_name ? 'is-invalid' : ''}`}
                                            placeholder="Enter first name"
                                            {...register('first_name')}
                                        />
                                        {errors.first_name && (
                                            <div className="invalid-feedback">{errors.first_name.message}</div>
                                        )}
                                    </div>

                                    {/* Last Name */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Last Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.last_name ? 'is-invalid' : ''}`}
                                            placeholder="Enter last name"
                                            {...register('last_name')}
                                        />
                                        {errors.last_name && (
                                            <div className="invalid-feedback">{errors.last_name.message}</div>
                                        )}
                                    </div>

                                    {/* Username */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Username <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                                            placeholder="Enter username"
                                            {...register('username')}
                                        />
                                        {errors.username && (
                                            <div className="invalid-feedback">{errors.username.message}</div>
                                        )}
                                    </div>

                                    {/* Date of Birth */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Date of Birth <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            className={`form-control ${errors.dob ? 'is-invalid' : ''}`}
                                            {...register('dob')}
                                        />
                                        {errors.dob && (
                                            <div className="invalid-feedback">{errors.dob.message}</div>
                                        )}
                                    </div>

                                    {/* Language */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Language <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className={`form-select ${errors.lang ? 'is-invalid' : ''}`}
                                            {...register('lang')}
                                        >
                                            <option value="">Select language</option>
                                            <option value="en">English</option>
                                            <option value="ar">Arabic</option>
                                        </select>
                                        {errors.lang && (
                                            <div className="invalid-feedback">{errors.lang.message}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact Information */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                Contact Information
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* Email */}
                                    <div className="col-md-12">
                                        <label className="form-label">
                                            Email <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                            placeholder="Enter email address"
                                            {...register('email')}
                                        />
                                        {errors.email && (
                                            <div className="invalid-feedback">{errors.email.message}</div>
                                        )}
                                    </div>

                                    {/* Phone Number with Country Code */}
                                    <div className="col-md-12">
                                        <label className="form-label">
                                            Phone Number <span className="text-danger">*</span>
                                        </label>
                                        <div className="d-flex gap-2">
                                            {/* Country Code Selector */}
                                            <div style={{ width: '150px' }}>
                                                <Controller
                                                    name="country_code"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <PhoneInput
                                                            country={'ae'} // Default to UAE
                                                            value={countryCode}
                                                            onChange={(phone, data) => {
                                                                setCountryCode(data.dialCode)
                                                                field.onChange(`+${data.dialCode}`)
                                                            }}
                                                            inputProps={{
                                                                name: 'country_code',
                                                                required: true,
                                                                disabled: true, // Disable typing
                                                            }}
                                                            containerStyle={{ width: '100%', height: '38px' }}
                                                            inputStyle={{ 
                                                                width: '100%', 
                                                                height: '38px',
                                                                fontSize: '14px',
                                                                borderColor: errors.country_code ? '#dc3545' : '#dee2e6',
                                                                cursor: 'pointer' // Show pointer cursor
                                                            }}
                                                            buttonStyle={{
                                                                borderColor: errors.country_code ? '#dc3545' : '#dee2e6'
                                                            }}
                                                            disableSearchIcon={false}
                                                        />
                                                    )}
                                                />
                                            </div>

                                            {/* Phone Number Input */}
                                            <div className="flex-grow-1">
                                                <input
                                                    type="tel"
                                                    className={`form-control ${errors.phone_number ? 'is-invalid' : ''}`}
                                                    placeholder="Enter phone number"
                                                    {...register('phone_number')}
                                                    onChange={(e) => {
                                                        const inputValue = e.target.value.replace(/\D/g, '')
                                                        if (inputValue.length <= 15) {
                                                            setValue('phone_number', inputValue)
                                                        }
                                                    }}
                                                    maxLength={15}
                                                    style={{ height: '38px' }}
                                                />
                                                {errors.phone_number && (
                                                    <div className="invalid-feedback">{errors.phone_number.message}</div>
                                                )}
                                            </div>
                                        </div>
                                        {errors.country_code && (
                                            <small className="text-danger d-block mt-1">{errors.country_code.message}</small>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Account Security */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                Account Security (Optional)
                            </div>
                            <div className="card-body p-4">
                                <div className="alert alert-info mb-3">
                                    <i className="fe-info me-2"></i>
                                    Leave password fields empty if you don't want to change the password
                                </div>
                                <div className="row g-3">
                                    {/* Password */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            New Password
                                        </label>
                                        <input
                                            type="password"
                                            className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                            placeholder="Enter new password"
                                            {...register('password')}
                                        />
                                        {errors.password && (
                                            <div className="invalid-feedback">{errors.password.message}</div>
                                        )}
                                        <small className="text-muted">
                                            Password must contain uppercase, lowercase, number, and special character
                                        </small>
                                    </div>

                                    {/* Confirm Password */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Confirm New Password
                                        </label>
                                        <input
                                            type="password"
                                            className={`form-control ${errors.confirm_password ? 'is-invalid' : ''}`}
                                            placeholder="Confirm new password"
                                            {...register('confirm_password')}
                                        />
                                        {errors.confirm_password && (
                                            <div className="invalid-feedback">{errors.confirm_password.message}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Emirates ID Documents */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                Emirates ID Documents
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* Front Image */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Emirates ID Front
                                        </label>
                                        <input
                                            type="file"
                                            className={`form-control ${errors.emirates_id_front ? 'is-invalid' : ''}`}
                                            accept="image/jpeg,image/jpg,image/png"
                                            {...register('emirates_id_front')}
                                        />
                                        {errors.emirates_id_front && (
                                            <div className="invalid-feedback">{errors.emirates_id_front.message}</div>
                                        )}
                                        <small className="text-muted d-block mt-1">
                                            Upload new image to replace existing one
                                        </small>
                                        
                                        {/* Show preview or existing */}
                                        <div className="mt-3">
                                            {frontPreview ? (
                                                <img
                                                    src={frontPreview}
                                                    alt="Front Preview"
                                                    style={{
                                                        width: '100%',
                                                        maxWidth: '300px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #dee2e6'
                                                    }}
                                                />
                                            ) : existingFrontImage ? (
                                                <img
                                                    src={existingFrontImage}
                                                    alt="Current Front"
                                                    style={{
                                                        width: '100%',
                                                        maxWidth: '300px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #dee2e6'
                                                    }}
                                                />
                                            ) : (
                                                <p className="text-muted">No image uploaded</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Back Image */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Emirates ID Back
                                        </label>
                                        <input
                                            type="file"
                                            className={`form-control ${errors.emirates_id_back ? 'is-invalid' : ''}`}
                                            accept="image/jpeg,image/jpg,image/png"
                                            {...register('emirates_id_back')}
                                        />
                                        {errors.emirates_id_back && (
                                            <div className="invalid-feedback">{errors.emirates_id_back.message}</div>
                                        )}
                                        <small className="text-muted d-block mt-1">
                                            Upload new image to replace existing one
                                        </small>
                                        
                                        {/* Show preview or existing */}
                                        <div className="mt-3">
                                            {backPreview ? (
                                                <img
                                                    src={backPreview}
                                                    alt="Back Preview"
                                                    style={{
                                                        width: '100%',
                                                        maxWidth: '300px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #dee2e6'
                                                    }}
                                                />
                                            ) : existingBackImage ? (
                                                <img
                                                    src={existingBackImage}
                                                    alt="Current Back"
                                                    style={{
                                                        width: '100%',
                                                        maxWidth: '300px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #dee2e6'
                                                    }}
                                                />
                                            ) : (
                                                <p className="text-muted">No image uploaded</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="d-flex gap-3 justify-content-end">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => navigate('/parents')}
                        disabled={isSubmitting}
                        style={{
                            padding: '10px 24px',
                            fontSize: '14px',
                            borderRadius: '6px'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-success"
                        disabled={isSubmitting}
                        style={{
                            padding: '10px 24px',
                            fontSize: '14px',
                            borderRadius: '6px',
                            minWidth: '120px'
                        }}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Updating...
                            </>
                        ) : (
                            'Update Parent'
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}

export default EditParent;